import { useRef, useEffect, useState } from 'react'
import { Divider, Switch, Tooltip, Popconfirm } from 'antd'
import classNames from 'classnames'
import styled from '@emotion/styled'
import type OSS from 'ali-oss'
import { Editor } from '@/pages/datastores/document-detail/paragraph/editor'
import type { Paragraph } from '@/pages/datastores/types/paragraph.ts'
import { MAX_SPLIT_SIZE } from '@/features/datastore/constant'
import { IconFont, usePageModal } from '@/components'
import { useModal } from '@/hooks/useModal.tsx'
import { ImagePreviewModal } from '@/components/modal/ImagePreviewModal.tsx'
import KeywordsView from '@/pages/datastores/components/KeywordsView.tsx'

export type ParagraphItem = Pick<
  Paragraph,
  | 'content'
  | 'isEdited'
  | 'enable'
  | 'key'
  | 'keywords'
  | 'extra_info'
  | 'chunk_id'
  | 'file_type'
  | 'file_name'
>

export interface ParagraphListItemProps {
  first?: boolean
  last?: boolean
  border?: boolean
  numText: string
  rank: number | string
  file_id: number
  activeId?: number
  paragraph: ParagraphItem
  onSplit?: (splitValue: [string, string]) => void
  onEnableChange: (enable: boolean) => void
  onEditorLoad: (editor: Editor) => void
  onEditorDestroy: () => void
  onEdit: () => void
  readonly?: boolean
  readyToMerge: boolean
  ossClient?: OSS
  onDelete: (key: number | string) => void
  updateParagraphsItemById?: (value: Paragraph) => void
  addParagraphsItem?: (item: Paragraph, targetId?: number) => void
  onSelect?: (key: number | string) => void
}

const Wrap = styled.div<{ disabled: boolean; isEdited?: boolean }>`
  &:hover {
    .info-box,
    .delete-btn {
      opacity: 1;
      width: auto;
    }
    .keywords-view {
      display: block;
    }
    .num-text {
      display: none;
    }
  }

  .paragraph-editor,
  .paragraph-editor > *:not(.paragraph-split-mark) {
    ${props => props.disabled && 'color: rgba(141, 141, 153, 0.6);'}
  }

  .edited-mark:before {
    content: '';
    width: 24px;
    height: 24px;
    background: #38a6ff;
    opacity: 0.4;
    position: absolute;
    top: -12px;
    left: -12px;
    pointer-events: none;
    display: ${props => (props.isEdited ? 'block' : 'none')};
    transform: rotate(45deg);
  }

  // em {
  //   color: red;
  //   font-style: normal;
  // }
  // table {
  //   width: 100%;
  // }

  // caption {
  //   color: blue;
  //   font-size: 14px;
  //   height: 20px;
  //   line-height: 20px;
  //   font-weight: 600;
  //   margin-bottom: 6px;
  // }

  // th {
  //   color: #fff;
  //   background-color: blue;
  // }

  // td:hover {
  //   background: blue;
  // }

  // tr:nth-child(even) {
  //   background-color: #f2f2f2;
  // }

  // p {
  //   position: absolute;
  // }

  // img {
  //   width: 100%;
  //   height: 100%;
  // }

  @keyframes show-paragraph-active {
    0% {
      background-color: hsla(240deg, 0%, 100%, 1);
    }

    20% {
      background-color: hsla(240deg, 7%, 90%, 1);
    }

    100% {
      background-color: hsla(240deg, 0%, 100%, 1);
    }
  }

  .paragraph-blink {
    animation: show-paragraph-active linear 1s 2;
  }
`

export default function ParagraphListItem(props: ParagraphListItemProps) {
  const {
    border,
    numText,
    paragraph,
    ossClient,
    file_id,
    readonly,
    activeId,
    onSplit,
    onEnableChange,
    onEdit,
    onEditorLoad,
    onEditorDestroy,
    onDelete,
    onSelect,
  } = props
  const editorRef = useRef<HTMLDivElement | null>(null)
  const editorInterface = useRef<Editor>()

  const pageModal = usePageModal()
  const [focus, setFocus] = useState(false)
  const [count, setCount] = useState(paragraph.content.length)
  const [readyToDelete, setReadyToDelete] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const moreThanMaxSize = count > MAX_SPLIT_SIZE

  const onImageUpload = async (file: File) => {
    if (ossClient) {
      const filePath = `i/${file_id}/${+new Date()}/${file.name}`

      ossClient.useBucket(import.meta.env.VITE_AI_DATASTORE_RESOURCE_BUCKET)
      const res = await ossClient.put(filePath, file)

      return res.url
    }
    throw new Error('oss client is not init')
  }

  const handleSelect = () => {
    onSelect?.((paragraph.chunk_id || (paragraph as any).originalChunkId) ?? 0)
  }

  const [imagePreviewModal] = useModal(ImagePreviewModal)
  const loadEditor = () => {
    editorInterface.current = new Editor({
      dom: editorRef.current!,
      content: paragraph.content,
      onImageUpload,
    })
    onEditorLoad(editorInterface.current)
    editorInterface.current.init()
    editorInterface.current.registryEvents('onFocus', () => setFocus(true))
    editorInterface.current.registryEvents('onBlur', () => setFocus(false))
    editorInterface.current.registryEvents('onLinkOpen', (url: string) => {
      pageModal.show({
        url,
      })
    })
    editorInterface.current.registryEvents('onImagePreview', (url: string) => {
      imagePreviewModal.open({ url })
    })
    setCount(editorInterface.current?.textCount ?? 0)
  }

  useEffect(() => {
    loadEditor()
    return () => {
      editorInterface.current?.destroy()
      onEditorDestroy()
    }
  }, [paragraph.content])

  useEffect(() => {
    if (editorInterface.current) {
      editorInterface.current.setEditEnable(!readonly)
    }
  }, [editorInterface.current, readonly])

  useEffect(() => {
    if (editorInterface.current) {
      editorInterface.current.registryEvents('onChange', () => {
        setCount(editorInterface.current?.textCount ?? 0)
        onEdit()
        if (!paragraph.enable) {
          onEnableChange(true)
        }
      })
    }
  }, [editorInterface.current, onEdit, onEnableChange])

  useEffect(() => {
    if (editorInterface.current && onSplit) {
      editorInterface.current.registryEvents(
        'onSplit',
        (values: [string, string]) => {
          // 拆分后重新渲染，以保证视图的状态和编辑器的状态同步
          editorInterface.current?.reRender(values[0])
          onSplit(values)
        },
      )
    }
  }, [editorInterface.current, onSplit])

  useEffect(() => {
    if (activeId !== paragraph.chunk_id) return
    handleSelect()
  }, [activeId])

  return (
    <Wrap
      disabled={!paragraph.enable}
      isEdited={paragraph.isEdited}
      className='pl-24 relative rounded-[8px] overflow-hidden betteryeah-markdown'
      onClick={handleSelect}
    >
      <div
        className={classNames(
          'edited-mark w-full h-full absolute left-24 top-0 overflow-hidden rounded-[8px] pointer-events-none',
        )}
      ></div>
      <div
        className={classNames('py-20 bg-white rounded-[8px] cursor-text', {
          'border-0': !border,
          border,
          'hover:b-primary': !readonly && border,
          'focus-within:b-primary': !readonly && border,
          '!b-error':
            (moreThanMaxSize && !readonly) ||
            readyToDelete ||
            deleteConfirmOpen,
          'paragraph-blink': activeId === paragraph.chunk_id,
        })}
      >
        <div className='flex-center-between px-20 gap-8 flex-1 w-full overflow-hidden'>
          <div className='w-full overflow-hidden'>
            <span
              className={classNames(
                'num-text text-[12px] !line-height-26px c-font_1 font-normal py-1 h-[26px]',
                {
                  hidden: !readonly && focus,
                },
              )}
            >
              {numText}
            </span>
            <div className={`${focus ? 'block' : 'hidden'} keywords-view`}>
              {paragraph.isEdited ? (
                <div className='h-[26px] flex items-center'>
                  <span className='font-normal text-[12px] c-font_1 line-height-20px'>
                    关键词
                  </span>
                  <span className='ml-8 font-normal line-height-20px text-[12px] c-font_1 c-op-60'>
                    【保存】后，重新生成关键词
                  </span>
                </div>
              ) : (
                <KeywordsView
                  value={paragraph.keywords ?? []}
                  className='flex flex-1'
                />
              )}
            </div>
          </div>
          <div className='flex items-center shrink-0'>
            <div
              className={classNames('flex items-center info-box', {
                'op-0': !focus,
              })}
            >
              <span
                className={classNames('c-font_1 text-[12px] font-normal', {
                  'c-error': moreThanMaxSize,
                })}
              >
                <span>共</span>
                <span className='mx-[3px]'>
                  <span
                    className={classNames({
                      'c-#000': focus,
                      '!c-error': moreThanMaxSize,
                    })}
                  >
                    {count}
                  </span>
                  {focus && !readonly && <span> / {MAX_SPLIT_SIZE}</span>}
                </span>
                <span>字</span>
              </span>
              {!paragraph.enable && (
                <div className='ml-12 py-2 text-12px px-8 rounded-4px c-#FE9700 bg-#FE9700 bg-op-12'>
                  <span className='w-8px h-8px rounded-full mr-6 inline-block bg-#FE9700'></span>
                  <span>已禁用</span>
                </div>
              )}
              {!readonly && (
                <>
                  <Divider type='vertical' className='mx-12' />
                  <Switch
                    size='small'
                    checked={paragraph.enable}
                    onChange={onEnableChange}
                  />
                </>
              )}
            </div>
            {!readonly && (
              <span
                className={classNames('flex items-center delete-btn', {
                  'op-0': !focus && !deleteConfirmOpen,
                })}
              >
                <Divider type='vertical' className='mx-12' />
                <Tooltip title='删除分段'>
                  <Popconfirm
                    icon={null}
                    title={<div className='p-10'>确定删除该分段吗？</div>}
                    open={deleteConfirmOpen}
                    onOpenChange={setDeleteConfirmOpen}
                    placement='bottom'
                    onConfirm={() => {
                      onDelete(paragraph.key)
                    }}
                    okButtonProps={{
                      danger: true,
                    }}
                    okText='确定'
                    cancelText='取消'
                  >
                    <span
                      className={classNames(
                        'rounded-4px w-24px h-24px hover:bg-error hover:bg-op-8 cursor-pointer text-16px  c-bg_3 c-op-48 hover:c-error flex-center',
                        {
                          'bg-error bg-op-8 c-error': deleteConfirmOpen,
                        },
                      )}
                      onMouseEnter={() => setReadyToDelete(true)}
                      onMouseLeave={() => setReadyToDelete(false)}
                      onClick={e => e.stopPropagation()}
                    >
                      <IconFont name='shanshu' />
                    </span>
                  </Popconfirm>
                </Tooltip>
              </span>
            )}
          </div>
        </div>
        <div className='mt-12 editor-wrap' ref={editorRef} />
      </div>
    </Wrap>
  )
}
